<template>
  <v-container fluid>
    <v-row>
      <v-col cols="9">
        <v-skeleton-loader :loading="loading" :types="skeleton" type="article" class="mb-6">
          <v-card class="elevation-1 mb-6 rounded-lg">
            <v-card-title class="pa-7">
              Objednávka
              <v-spacer></v-spacer>
              <OrderStatus :max="false" :status="order.statusCode" />
            </v-card-title>
            <v-card-text class="pa-7">
              <v-row>
                <v-col cols="12">
                  <v-row class="pb-6">
                    <v-col cols="4" class="py-0">
                      <div class="request-label">ID</div>
                      <div class="request-value mt-2">
                        {{ order.id }}
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-row class="pb-6">
                    <v-col cols="4" class="py-0">
                      <div class="request-label">Datum</div>
                      <div class="request-value mt-2">
                        <DateTime :datetime="order.createdAt" :single-line="false" />
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-row class="pb-6">
                    <v-col cols="4" class="py-0">
                      <div class="request-label">Typ</div>
                      <div class="request-value mt-2">
                        {{ order.type ? order.type.text : '-' }}
                      </div>
                    </v-col>
                    <v-col v-if="order.pageReferer" cols="4" class="py-0">
                      <div class="request-label">Formulář</div>
                      <div class="request-value mt-2">
                        <a target="_blank" :href="order.pageReferer"> Otevřít </a>
                        <v-icon small color="primary">mdi-open-in-new</v-icon>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <div v-if="order.promotion" class="mt-3">
                <v-row>
                  <v-col cols="12">
                    <v-row class="pb-6">
                      <v-col cols="4" class="py-0">
                        <div class="request-label">Propagace</div>
                        <div class="request-value mt-2">
                          <router-link
                            :to="{
                              name: 'promotions.detail',
                              params: {
                                promotionId: order.promotion.id,
                              },
                            }"
                          >
                            {{ order.promotion.id }}
                          </router-link>
                        </div>
                      </v-col>
                      <v-col v-if="order.promotion.gift" cols="4" class="py-0">
                        <div class="request-label">Dárek</div>
                        <div class="request-value mt-2">
                          <router-link
                            :to="{
                              name: 'gifts.detail',
                              params: {
                                giftId: order.promotion.gift.id,
                              },
                            }"
                          >
                            {{ order.promotion.gift.name }}
                          </router-link>
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
              <div v-if="order.offer" class="mt-3">
                <v-row>
                  <v-col cols="12">
                    <v-row class="pb-6">
                      <v-col cols="4" class="py-0">
                        <div class="request-label">Nabídka</div>
                        <div class="request-value mt-2">
                          <router-link
                            :to="{
                              name: 'personalized-offer.detail',
                              params: {
                                offerId: order.offer.id,
                              },
                            }"
                          >
                            {{ order.offer.id }}
                          </router-link>
                        </div>
                      </v-col>
                      <v-col cols="4" class="py-0">
                        <div class="request-label">Název</div>
                        <div class="request-value mt-2">
                          {{ order.offer.name }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
              <div v-if="order.labels.length" class="mt-3">
                <v-row class="pl-2 pr-2">
                  <v-col
                    v-for="label in order.labels"
                    :key="label.id"
                    cols="auto"
                    class="col-0 pa-1"
                  >
                    <OrderLabel :label="label" @labelDeleted="loadOrder()" />
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-card>
        </v-skeleton-loader>
        <v-skeleton-loader :loading="loading" :types="skeleton" type="article" class="mb-6">
          <v-row>
            <v-col>
              <v-card class="elevation-1 rounded-lg">
                <v-card-title class="pa-7">
                  Zákazník
                  <v-spacer></v-spacer>
                  <UserRole
                    v-if="order.customer.user"
                    :role="order.customer.user.role"
                    class="mr-1"
                  />
                  <UserStatus v-if="order.customer.user" :status="order.customer.user.status" />
                </v-card-title>
                <v-card-text class="pa-7">
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="6" class="py-0">
                          <div class="request-label">ID</div>
                          <div class="request-value mt-2">
                            <router-link
                              v-if="order.customer"
                              :to="{
                                name: 'customers.detail',
                                params: {
                                  customerId: order.customer.id,
                                },
                              }"
                            >
                              {{ order.customer.id }}
                            </router-link>
                          </div>
                        </v-col>
                        <v-col cols="6" class="py-0">
                          <div class="request-label">Daktela</div>
                          <div v-if="order.customer.externalId" class="request-value mt-2">
                            <a
                              target="_blank"
                              :href="`https://enkidoo.daktela.com/crm/contacts/update/${order.customer.externalId}`"
                            >
                              Zobrazit
                            </a>
                            <v-icon small color="primary">mdi-open-in-new</v-icon>
                          </div>
                          <div v-else class="request-value mt-2">-</div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="6" class="py-0">
                          <div class="request-label">Jméno</div>
                          <div class="request-value mt-2">
                            {{
                              order.customer && order.customer.fullName
                                ? order.customer.fullName
                                : '-'
                            }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="6" class="py-0">
                          <div class="request-label">Datum narození</div>
                          <div class="request-value mt-2">
                            {{
                              order.customer && order.customer.birthday
                                ? $dayjs(order.customer.birthday).format('D.M.YYYY')
                                : '-'
                            }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="pb-6">
                        <v-col cols="6" class="py-0 pb-6">
                          <div class="request-label">Telefon</div>
                          <div class="request-value mt-2">
                            {{
                              order.customer && order.customer.phone
                                ? formatPhoneNumber(order.customer.phone)
                                : '-'
                            }}
                          </div>
                        </v-col>
                        <v-col cols="auto" class="py-0">
                          <div class="request-label">E-mail</div>
                          <div class="request-value mt-2">
                            {{
                              order.customer && order.customer.email ? order.customer.email : '-'
                            }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col>
              <CustomerDaktelaLastActivities v-if="order.customer.id" :id="order.customer.id" />
            </v-col>
          </v-row>
        </v-skeleton-loader>
        <v-skeleton-loader
          v-if="order.moveOrder"
          :loading="loading"
          :types="skeleton"
          type="article"
          class="mb-6"
        >
          <OrderMoveDetail v-model="order.moveOrder" />
        </v-skeleton-loader>
        <v-skeleton-loader
          v-if="order.questionOrder"
          :loading="loading"
          :types="skeleton"
          type="article"
          class="mb-6"
        >
          <OrderQuestionDetail v-model="order.questionOrder" />
        </v-skeleton-loader>
        <v-skeleton-loader
          v-if="order.electricityOrder"
          :loading="loading"
          :types="skeleton"
          type="article"
          class="mb-6"
        >
          <OrderElectricityDetail :id="$route.params.orderId" v-model="order.electricityOrder" />
        </v-skeleton-loader>
        <v-skeleton-loader
          v-if="order.electricityWorkOrder"
          :loading="loading"
          :types="skeleton"
          type="article"
          class="mb-6"
        >
          <OrderElectricityWorkDetail
            :id="$route.params.orderId"
            v-model="order.electricityWorkOrder"
            :new-om="order.type.name === 'ELECTRICITY_WORK_OM_NEW'"
          />
        </v-skeleton-loader>
        <v-skeleton-loader
          v-if="order.gasOrder"
          :loading="loading"
          :types="skeleton"
          type="article"
          class="mb-6"
        >
          <OrderGasDetail :id="$route.params.orderId" v-model="order.gasOrder" />
        </v-skeleton-loader>
        <OrderSignatureDocuments v-if="order.id" v-model="order" />
        <OrderComments v-if="order.id" :id="$route.params.orderId" />
        <OrderAttachments v-if="order.id" :id="$route.params.orderId" />
        <OrderPromoCodes v-if="order.id" :id="$route.params.orderId" />
        <OrderOffers v-if="order.id" :id="$route.params.orderId" />
        <OrderRevisions v-if="order.id" :id="$route.params.orderId" :status="order.statusCode" />
      </v-col>
      <v-col cols="3">
        <OrderNotificationEmailForm v-if="order.id" v-model="order" @update="onUpdateAction" />
        <OrderNotificationSmsForm v-if="order.id" v-model="order" @update="onUpdateAction" />
        <OrderActionForm v-if="order.id" v-model="order" @update="onUpdateAction" />
        <OrderStatusForm v-if="order.id" v-model="order" title="Stav" @update="onUpdateStatus" />
        <OrderStatusForm
          v-if="order.id"
          v-model="order"
          title="Stav - admin"
          :all="true"
          @update="onUpdateStatus"
        />
      </v-col>
    </v-row>
    <OrderLabelAddDialog @labelAdded="loadOrder()" />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import OrderRevisions from '@/components/revision/OrderRevisions'
import OrderSignatureDocuments from '@/components/order/OrderSignatureDocuments'
import OrderGasDetail from '@/views/pages/order/OrderGasDetail'
import OrderElectricityDetail from '@/views/pages/order/OrderElectricityDetail'
import OrderStatus from '@/components/order/OrderStatus'
import { formatPhoneNumber } from '@/components/mixins/formatPhoneNumber'
import OrderStatusForm from '@/components/forms/OrderStatusForm'
import DateTime from '@/components/DateTime'
import OrderMoveDetail from '@/views/pages/order/OrderMoveDetail'
import OrderQuestionDetail from '@/views/pages/order/OrderQuestionDetail'
import OrderActionForm from '@/components/forms/OrderActionForm'
import OrderComments from '@/components/order/OrderComments'
import OrderAttachments from '@/components/order/OrderAttachments'
import OrderNotificationEmailForm from '../../../components/forms/OrderNotificationEmailForm'
import OrderNotificationSmsForm from '../../../components/forms/OrderNotificationSmsForm'
import OrderElectricityWorkDetail from './OrderElectricityWorkDetail'
import OrderPromoCodes from '../../../components/order/OrderPromoCodes'
import UserRole from '@/components/customer/UserRole'
import UserStatus from '@/components/customer/UserStatus'
import OrderLabel from '../../../components/order/OrderLabel'
import OrderLabelAddDialog from '../../../components/order/OrderLabelAddDialog'
import OrderOffers from '../../../components/order/OrderOffers'
import CustomerDaktelaLastActivities from '../../../components/customer/CustomerDaktelaLastActivities.vue'

export default {
  name: 'OrderDetail',
  components: {
    CustomerDaktelaLastActivities,
    OrderOffers,
    UserStatus,
    UserRole,
    OrderLabelAddDialog,
    OrderLabel,
    OrderPromoCodes,
    OrderElectricityWorkDetail,
    OrderNotificationSmsForm,
    OrderNotificationEmailForm,
    OrderAttachments,
    OrderComments,
    OrderActionForm,
    OrderQuestionDetail,
    OrderMoveDetail,
    DateTime,
    OrderStatusForm,
    OrderStatus,
    OrderElectricityDetail,
    OrderGasDetail,
    OrderSignatureDocuments,
    OrderRevisions,
  },
  mixins: [formatPhoneNumber],
  data() {
    return {
      skeleton: {
        article: 'heading, paragraph@2',
      },
      order: {
        electricityOrder: {},
        electricityWorkOrder: {},
        gasOrder: {},
        moveOrder: {},
        questionOrder: {},
      },
      loading: false,
      saving: false,
    }
  },
  created() {
    this.loadOrder()
  },
  methods: {
    ...mapActions({
      getOrder: 'order/getOrder',
    }),
    loadOrder() {
      this.loading = true
      this.getOrder(this.$route.params.orderId)
        .then((response) => {
          this.order = response.data
        })
        .finally(() => {
          this.loading = false
        })
    },
    onUpdateStatus() {
      this.loadOrder()
    },
    onUpdateAction() {
      this.loadOrder()
    },
    back() {
      this.$router.push({
        name: 'orders',
      })
    },
  },
}
</script>
